import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { VoiceTextarea } from '@/components/ui/voice-textarea';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderData } from '@/hooks/useHeaderData';
import { loadCompanyConversation, useCompanyConversations } from '@/hooks/useCompanyConversations';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { supabase } from '@/integrations/supabase/client';
import { trackActivity } from '@/lib/activityTracking';
import { playSendSound } from '@/lib/messageSounds';

export const FEATURE_META: FeatureMeta = {
  id: 'desk-inline-reply', label: 'Reply here',
  description: 'Read the latest incoming company message and explicitly send a reply without leaving Your Next Move',
  triggerType: 'editor', personas: ['seller', 'buyer_seller'],
};

export default function DeskInlineReply({ companyId, onClose, onSent }: {
  companyId: string; onClose: () => void; onSent: () => void;
}) {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const { data: header } = useHeaderData();
  const qc = useQueryClient();
  const companies = useCompanyConversations();
  const company = companies.data?.find(c => c.company_id === companyId);
  const conversation = useQuery({
    queryKey: ['desk-reply-conversation', user?.id, companyId],
    enabled: !!company,
    queryFn: () => loadCompanyConversation(companyId),
    staleTime: 0,
  });
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const sendingRef = useRef(false);
  const incoming = conversation.data?.filter(m => m.sender_company_id === companyId).at(-1);
  const parent = conversation.data?.at(-1);
  const loading = companies.isLoading || (!!company && conversation.isLoading);
  const unavailable = companies.isError || (!loading && !company) || conversation.isError;

  const send = async () => {
    if (sendingRef.current || !user || !header?.companyId || !company || !parent || !text.trim()) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    try {
      // Same authorized company-message write as Messages; no AI action or new access path.
      const { error } = await supabase.from('platform_messages').insert({
        sender_user_id: user.id, sender_company_id: header.companyId,
        recipient_company_id: company.company_id, parent_message_id: parent.id,
        content: text.trim(), listing_id: null,
      });
      if (error) throw error;
      trackActivity('message_sent', { counterparty_company_id: company.company_id, origin: 'company_thread' });
      playSendSound();
      toast.success('Reply sent', { description: `Your message is in the conversation with ${company.company_name}.` });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['company-conversations'] }),
        qc.invalidateQueries({ queryKey: ['desk-next-move'] }),
        qc.invalidateQueries({ queryKey: ['desk-habit'] }),
      ]);
      onSent();
    } catch {
      setSendError('Your reply wasn’t sent. Please try again; your draft is still here.');
    } finally { sendingRef.current = false; setSending(false); }
  };

  return <div className="mt-5 border-t border-border pt-4" aria-label="Reply to company">
    <div className="mb-3 flex items-center justify-between gap-2">
      <p className="text-sm font-medium">{company ? `Reply to ${company.company_name}` : 'Reply'}</p>
      <Button variant="ghost" size="icon-sm" aria-label="Close reply" disabled={sending} onClick={onClose}><X className="h-4 w-4" /></Button>
    </div>
    {loading ? <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Getting the conversation…</p> : unavailable ?
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="alert" className="text-sm text-destructive">We couldn’t open this conversation here.</p>
        <Button asChild variant="outline" size="sm"><Link to={`/messages?with=${companyId}`}>Open in Messages<ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
      </div> : <>
      {incoming && <blockquote className="mb-3 max-h-32 overflow-y-auto border-l-2 border-border pl-3 text-sm text-muted-foreground whitespace-pre-wrap">{incoming.content}</blockquote>}
      <VoiceTextarea onValueChange={setText} autoFocus aria-label="Your reply" placeholder="Write your reply…" value={text} onChange={e => setText(e.target.value)} disabled={sending} maxLength={2000} rows={3} className="resize-none text-base md:text-sm" />
      {sendError && <p role="alert" className="mt-2 text-sm text-destructive">{sendError}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground"><Link to={`/messages?with=${companyId}`}>Full conversation<ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
        <Button variant="guide" size="sm" onClick={() => void send()} disabled={sending || !text.trim() || !parent || !header?.companyId}>{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Send reply</Button>
      </div>
    </>}
  </div>;
}